import React from 'react';
import './chatmessage.css';
import { Role } from '../../api/llmapi';
import { Conversation } from '../../../fasades/conversation';
import { Message } from '../../chat/message';
import { FiEdit3 } from "react-icons/fi";
import { GiWeightLiftingUp } from "react-icons/gi";
import { IoRefresh } from "react-icons/io5";
import { HiCheck } from "react-icons/hi";
import { MdDeleteForever } from "react-icons/md";
import { MdOutlineCancel } from "react-icons/md";
import { Util } from '../../../fasades/util';
import { Thinking } from './thinking';
import { TextAreaResize } from '../textarearesize';
import { Key } from './key';
import { OneOf } from '../oneof';

interface ChatMessageProps {
    message : Message;
}

class ChatMessage extends React.Component<ChatMessageProps> 
{
    edit         : boolean = false;
    textareaRef  : React.RefObject<TextAreaResize | null>;

    constructor(props: ChatMessageProps) {
        super(props);
        this.textareaRef = React.createRef();
    }

    disableTraining = () : boolean => {
        return !this.props.message.canTrain();
    }

    onPrevSibling = () => {
        const msg = this.props.message;
        msg.selectSibling(msg.index - 1);
        Conversation.forceUpdate();
    }

    onNextSibling = () => {
        const msg = this.props.message;
        msg.selectSibling(msg.index + 1);
        Conversation.forceUpdate();
    }

    onDelete = () => {
        const msg = this.props.message;
        if (msg.parent !== undefined) {
            msg.parent.deleteChild(msg);
            Conversation.forceUpdate();
        }
        else {
            Conversation.newChat();
            Conversation.forceUpdate();
        }
    }

    onCancelEdit() 
    {
        this.edit = false;
        Conversation.forceUpdate();
    }

    onEdit = () => {
        this.edit = true;
        Conversation.forceUpdate();
    }

    onEndEdit = () => {
        this.edit = false;
        if (this.textareaRef.current) {
            this.props.message.content   = this.textareaRef.current.getText();
            this.props.message.timestamp = new Date();
        }
        Conversation.forceUpdate();
    }

    onRefresh = () => {
        const msg      = this.props.message;
        const messages = msg.getPriorMessages();
        Conversation.getReply(messages);
    }

    onRating = (rating : number) => {
        const msg      = this.props.message;
        msg.rating = rating;
        Conversation.forceUpdate();
    }

    onTrain = () => {
        Conversation.train(this.props.message);
    }

    form(text : string, showCursor : boolean) : React.JSX.Element[]
    {
        if (this.edit) {
            return this.formEdit(text);
        }

        let key        = new Key();
        let thinkStart = text.indexOf("<think>");
        if (thinkStart === -1) {
            return this.formPage(text, key, showCursor);
        }
        thinkStart = this.removeWhite(text, thinkStart + 7);
        let thinkEnd = text.indexOf("</think>");
        if (thinkEnd === -1) {
            let element : React.JSX.Element[] = [];
            element.push(<Thinking key={`code-${key.get()}`}>{ this.formPage(text.substring(thinkStart), key, showCursor) }</Thinking>);
            return element;
        }

        let answerStart = this.removeWhite(text, thinkEnd + 8);

        if (thinkStart === thinkEnd) {
            return this.formPage(text.substring(answerStart), key, showCursor);
        }

        let thinking = text.substring(thinkStart, thinkEnd);
        let answer   = text.substring(answerStart);
        let elements : React.JSX.Element[] = [];
        elements.push(<Thinking key={`code-${key.get()}`}>{ this.formPage(thinking, key, false) }</Thinking>);
        elements.push(...this.formPage(answer, key, showCursor));

        return elements;
    }

    formPage(text : string, key : Key, displayCursor : boolean) : React.JSX.Element[]
    {
        let start = 0;
        let elements : React.JSX.Element[] = [];

        while(true) {
            if (start >= text.length) {
                return elements;
            }

            let codeStart = text.indexOf("```", start);
            if (codeStart === -1) {
                const paragraphElements = this.formParagraphs(text.substring(start, text.length), key, displayCursor);
                elements.push(...paragraphElements);
                return elements;
            }
            if (start < codeStart) {
                const paragraphElements = this.formParagraphs(text.substring(start, codeStart), key, false);
                elements.push(...paragraphElements);
            }
            codeStart += 3;

            let codeEnd = text.indexOf("```", codeStart);
            if (codeEnd === -1) {
                elements.push(<pre className="message-code" key={`code-${key.get()}`}>{ text.substring(codeStart) } </pre>);
                return elements;
            }

            elements.push(<pre className="message-code" key={`code-${key.get()}`}>{ text.substring(codeStart, codeEnd) } </pre>);
            start = codeEnd + 3;
        }
    }

    formEdit(text : string) : React.JSX.Element[]
    {
        return [<TextAreaResize
          key="edit-textarea"
          ref={this.textareaRef}
          text={text}
          editMode={true}
          className="message-edit"
          style={{
            minHeight: "40px",
            maxHeight: "200px"
          }}/>];
    }

    formParagraphs(text : string, key : Key, displayCursor : boolean) :  React.JSX.Element[]
    {
        let elements : React.JSX.Element[] = [];
        let childKey : Key                 = new Key();

        const paragraphs = text.split('\n\n');
        for (let i = 0; i < paragraphs.length; i++) {
            const lines = paragraphs[i].split('\n');
            const content : React.ReactNode[] = [];

            for (let j = 0; j < lines.length; j++) {
                if (j > 0) {
                    content.push(<br key={`br-${childKey.get()}-${i}-${j}`}/>);
                }
                content.push(...this.splitLine(lines[j], childKey, displayCursor && (i + 1 === paragraphs.length) && (j + 1) === lines.length));
            }

            elements.push(<p key={`p-${key.get()}-${i}`}>{content}</p>);
        }

        return elements;
    }

    getClasses() : string
    {
        let classes = 'message';
        if (this.props.message.role === Role.User) {
            classes += ' user-message';
        }
        else {
            classes += ' bot-message';
        }

        if (this.edit) {
            classes += ' edit';
        }

        return classes;
    }

    splitLine(lineText : string, key : Key, displayCursor : boolean) : React.JSX.Element[]
    {
        let elements : React.JSX.Element[] = [];

        let start = 0;

        while(start < lineText.length) {
            const boldStart = lineText.indexOf('**', start);
            if (boldStart === -1) {
                elements.push(<span key={key.get()}>{lineText.substring(start)}</span>);
                break;
            }

            const boldEnd = lineText.indexOf('**', boldStart + 2);
            if (boldEnd === -1) {
                elements.push(<span key={key.get()}>{lineText.substring(start)}</span>);
                break;
            }

            if (boldStart > start) {
                elements.push(<span key={key.get()}>{lineText.substring(start, boldStart)}</span>);
            }

            elements.push(<strong key={key.get()}>{lineText.substring(boldStart+2, boldEnd)}</strong>);
            start += boldEnd+2;
        }

        if (displayCursor) {
            elements.push(<span key={key.get()} className="cursor">{'█'}</span>);
        }

        return elements;
    }

    formStarRating = (rating : number) : React.JSX.Element => {
        return (
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`star ${star <= rating ? 'filled' : ''}`}
                  onClick={() => this.onRating(star)}>★</span>
              ))}
            </div>);
        }

    render() {
        const { message }   = this.props;
        const formattedTime = Util.formatRelativeTime(message.timestamp);
        const paragraphs    = this.form(message.content, message.showCursor);

        return (
          <div className={ this.getClasses() }>
            <div className={`message-content ${this.edit ? 'message-edit' : ''}`}>
                <>{paragraphs}</>
                { this.renderActions(message) }
            </div>
            { (message.role === Role.Assistant) && this.renderOneOf(message) }
            { (message.role === Role.Assistant) && (
                <>{this.formStarRating(message.rating)}</>
            )}
            <div className="message-timestamp">{formattedTime}</div>
          </div>
        );
    }

    renderActions(message : Message) : React.JSX.Element
    {
        return (
            <div className="message-actions">
              {message.role === Role.Assistant && !this.edit && (
                <>
                <button className="refresh-button" title="Generate New Version" onClick={this.onRefresh}>
                    {React.createElement(IoRefresh as React.ComponentType)}
                </button>
                <button title="Training" onClick={this.onTrain}>
                    {React.createElement(GiWeightLiftingUp as React.ComponentType)}
                </button>
                </>
              )}
              {!this.edit && (
                <>
                <button title="Edit" onClick={() => this.onEdit()}>
                    {React.createElement(FiEdit3 as React.ComponentType)}
                </button>
                <button title="Delete" onClick={() => this.onDelete()}>
                    {React.createElement(MdDeleteForever as React.ComponentType)}
                </button>
                </>)
              }
              {this.edit && (
                <>
                <button title="Confirm edit" onClick={() => this.onEndEdit()}>
                    {React.createElement(HiCheck as React.ComponentType)}
                </button>
                <button className="redbutton" title="Cancel edit" onClick={() => this.onCancelEdit()}>
                    {React.createElement(MdOutlineCancel as React.ComponentType)}
                </button>
                </>)
              }
            </div>);
    }

    renderOneOf(message : Message) : React.JSX.Element
    {
        const siblings = message.getSiblings();

        return (
            <OneOf
                index  = {message.index}
                n      = { siblings }
                onPrev = { (index : number) => this.onPrevSibling() }
                onNext = { (index : number) => this.onNextSibling() }
            />
        );
    }

    removeWhite(text : string, start : number)
    {
        while(start < text.length) {
            const ch = text[start];

            if (ch === ' ' || ch === '\t' || ch === '\r' || ch === '\n' || ch === '\v' || ch === '\f') {
                start++;
                continue;
            }

            return start;
        }

        return start;
    }
}

export default ChatMessage;

